import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, MenuIcon, PercentIcon, PhoneIcon, ShoppingCartIcon, UserIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { SearchBar } from './SearchBar';
import { MegaMenuPanel } from './MegaMenuPanel';
import { useCart } from '../../contexts/CartContext';
import { megaMenu, navLinks } from '../../data/navigation';
export function Header() {
  const {
    count,
    openCart
  } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [megaId, setMegaId] = useState<string | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>();
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    setMenuOpen(false);
    setMegaId(null);
  }, [pathname]);
  useEffect(() => {
    if (!megaId) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMegaId(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [megaId]);
  const openWithDelay = (id: string) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setMegaId(id), megaId ? 0 : 120);
  };
  const closeMega = () => {
    clearTimeout(hoverTimer.current);
    setMegaId(null);
  };
  const active = megaMenu.find(m => m.id === megaId);
  return <header className="sticky top-0 z-40 border-b border-line bg-white" data-id="element-248" data-mp="3k41fn28l8xnk">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-249" data-mp="3p22zvqhr330m">
        <div className="flex h-16 items-center gap-3 lg:h-[76px] lg:gap-10" data-id="element-250" data-mp="3u0mlfpsfmcsg">
          <button type="button" onClick={() => setMenuOpen(o => !o)} className="-ml-2 flex h-10 w-10 items-center justify-center rounded-lg text-ink hover:bg-surface lg:hidden" aria-label={menuOpen ? 'Menu sluiten' : 'Menu openen'} aria-expanded={menuOpen} data-id="element-251" data-mp="3u2qzv1nyiu6z">
            {menuOpen ? <XIcon className="h-5 w-5" data-id="element-252" data-mp="3ablmsm95x2fz" /> : <MenuIcon className="h-5 w-5" data-id="element-253" data-mp="3aak1loyfmzkj" />}
          </button>

          <Logo data-id="element-254" data-mp="35e3d2tnbmj6x" />

          <div className="hidden max-w-2xl flex-1 md:block" data-id="element-255" data-mp="35cijp7cqt6an">
            <SearchBar data-id="element-256" data-mp="3jcteglh6b513" />
          </div>

          <div className="ml-auto flex items-center gap-1" data-id="element-257" data-mp="3jcjj0idd05o0">
            <a href="tel:+31851234567" className="mr-3 hidden items-center gap-2.5 xl:flex" data-id="element-258" data-mp="3edgqfxekfrwx">
              <PhoneIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-259" data-mp="2qo4fot3lbbsi" />
              <span className="leading-tight" data-id="element-260" data-mp="2qm1gbigjs6b4">
                <span className="block text-xs text-muted" data-id="element-261" data-mp="2mnu0cptwr90z">Deskundig advies</span>
                <span className="block whitespace-nowrap text-sm font-semibold text-ink" data-id="element-262" data-mp="30eqp25kpr62r">085 123 4567</span>
              </span>
            </a>
            <Link to="/" className="flex h-10 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-ink hover:bg-surface" data-id="element-263" data-mp="2vm60bignzj63">
              <UserIcon className="h-5 w-5" aria-hidden="true" data-id="element-264" data-mp="22ytcl4yrp5b7" />
              <span className="hidden sm:inline" data-id="element-265" data-mp="22yjltf5weu4g">Inloggen</span>
            </Link>
            <button type="button" onClick={openCart} className="relative flex h-10 items-center gap-2 rounded-lg bg-ink px-3.5 text-sm font-semibold text-white hover:bg-ink-soft" aria-label={`Winkelwagen openen, ${count} artikelen`} data-id="element-266" data-mp="ifzdu2b3h2yn">
              <ShoppingCartIcon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-267" data-mp="efywa8sp8bnd" />
              <span className="hidden sm:inline" data-id="element-268" data-mp="125taator8697">Winkelwagen</span>
              <motion.span key={count} initial={{
              scale: 0.7
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 600,
              damping: 20
            }} className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-volt px-1.5 text-[11px] font-bold text-ink" data-id="element-269" data-mp="1663fw58mwunj">
                {count}
              </motion.span>
            </button>
          </div>
        </div>

        <div className="pb-3 md:hidden" data-id="element-270" data-mp="3hx7bekw1kxee">
          <SearchBar data-id="element-271" data-mp="10op2yj6d20o" />
        </div>
      </div>

      {/* Desktop navigation with mega menu */}
      <nav className="relative hidden border-t border-line lg:block" aria-label="Categorieën" onMouseLeave={closeMega} data-id="element-272" data-mp="3sf2r8pwmoqnz">
        <ul className="mx-auto flex h-12 max-w-7xl items-center gap-1 px-6 text-sm font-semibold" data-id="element-273" data-mp="3oeakc3b8axuv">
          {megaMenu.map(m => {
          const isOpen = megaId === m.id;
          const isCurrent = pathname.startsWith(m.href);
          return <li key={m.id} onMouseEnter={() => openWithDelay(m.id)} data-id="element-274" data-mp="fxfqtegbmptg">
                <button type="button" onClick={() => setMegaId(isOpen ? null : m.id)} aria-expanded={isOpen} aria-haspopup="true" className={`relative flex h-12 items-center gap-1 whitespace-nowrap px-3 transition-colors duration-150 ${isOpen || isCurrent ? 'text-ink' : 'text-ink/80 hover:text-ink'} ${isOpen || isCurrent ? 'after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-ink' : ''}`} data-id="element-275" data-mp="fy79xpsqev2u">
                  {m.label}
                  <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-276" data-mp="2llepuoij50g9" />
                </button>
              </li>;
        })}
          <li onMouseEnter={closeMega} data-id="element-277" data-mp="2zctf3d315xvr">
            <NavLink to="/shop" end className="flex h-12 items-center whitespace-nowrap px-3 text-ink/80 hover:text-ink" data-id="element-278" data-mp="2ze4fd2x9oni4">
              Alle producten
            </NavLink>
          </li>
          <li className="ml-auto hidden xl:block" onMouseEnter={closeMega} data-id="element-279" data-mp="39ar25x95loms">
            <Link to="/#advisor" className="flex h-12 items-center whitespace-nowrap px-3 font-medium text-muted hover:text-ink" data-id="element-280" data-mp="3e89bhor325vb">
              Batterijadvies
            </Link>
          </li>
          <li className="ml-auto xl:ml-0" onMouseEnter={closeMega} data-id="element-281" data-mp="1juhvm7i5uprf">
            <NavLink to="/deals" className="ml-2 flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full bg-volt px-3.5 text-ink transition-colors duration-150 hover:bg-volt-hover" data-id="element-282" data-mp="1bulgmuh5kfbk">
              Aanbiedingen
              <PercentIcon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" data-id="element-283" data-mp="1lucf178u3egx" />
            </NavLink>
          </li>
        </ul>

        <AnimatePresence data-id="element-284" data-mp="1utfxqa439ruw">
          {active && <motion.div key="mega" initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.16,
          ease: [0.23, 1, 0.32, 1]
        }} className="absolute inset-x-0 top-full border-t border-line bg-white shadow-[0_24px_48px_-24px_rgba(14,26,36,0.28)]" data-id="element-285" data-mp="1us4t682tkg2q">
              <MegaMenuPanel item={active} onNavigate={closeMega} data-id="element-286" data-mp="2dkgwax1arldx" />
            </motion.div>}
        </AnimatePresence>
      </nav>

      <AnimatePresence data-id="element-287" data-mp="2s6mnm9vpaojh">
        {menuOpen && <motion.nav initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden border-t border-line lg:hidden" aria-label="Categorieën mobiel" data-id="element-288" data-mp="2s7xre9p5bwal">
            <ul className="px-4 py-2 sm:px-6" data-id="element-289" data-mp="29gel9tzts24c">
              {[{
            label: 'Alle producten',
            href: '/shop'
          }, ...navLinks, {
            label: 'Aanbiedingen',
            href: '/deals'
          }].map(link => <li key={link.label} className="border-b border-line last:border-0" data-id="element-290" data-mp="20fgj7h8y64oe">
                  <Link to={link.href} className={`block py-3 text-sm font-medium ${link.label === 'Aanbiedingen' ? 'text-sale' : 'text-ink'}`} data-id="element-291" data-mp="20ddlsc5gt99n">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}